import { useReactiveVar } from '@apollo/client'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react'

import { RADIUS_SLIDER_HALF_POINT_KMS } from '@/constants/location'
import { areLocationsEquivalent } from '@/domain/location/areLocationsEquivalent'
import { setModifiedRadiusLocalStorage } from '@/domain/location/setModifiedRadiusLocalStorage'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useSearchActions } from '@/hooks/srp/useSearchActions'
import { type GlobalLocation, searchLocationVar } from '@/lib/apollo/locationCache'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { isPositiveInteger } from '@/utils/kijiji-common/number'
import { setGlobalLocation } from '@/utils/location/location'

import { LocationModalFooter } from './LocationModalFooter'
import { LocationModalHeader } from './LocationModalHeader'
import { LocationSearchBar } from './LocationSearchBar'
import { Map } from './Map'
import { RegionCheckbox } from './RegionCheckbox'
import { LocationSearchBarWrapper, StyledModal, StyledModalBody } from './styled'

export type SearchLocationModalProps = {
  /**
   * Passes function to open the modal to children
   */
  children: (openModal: () => void) => ReactNode
}

export const SearchLocationModal: FC<SearchLocationModalProps> = ({ children }) => {
  const { pathname } = useRouter()
  const { data } = useGetSearchResultsData({ fetchPolicy: 'cache-only' })
  const { refetchResults } = useSearchActions(data?.searchQuery?.filters)

  const { t } = useTranslation(['common', 'global_header'])
  const globalLocation = useReactiveVar(searchLocationVar)
  const [localLocation, setLocalLocation] = useState<GlobalLocation>(globalLocation)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchByRegionCheckboxValue, setSearchByRegionCheckboxValue] = useState<boolean>(false)

  const [isOpen, setIsOpen] = useState<boolean>(false)

  /** Update the local location once the reactive var is initiated */
  useEffect(() => {
    setLocalLocation(globalLocation)
  }, [globalLocation])

  /**
   * Show map if input is not focused, and we are not searching by region
   */
  const showMap = searchByRegionCheckboxValue ? true : !localLocation?.isRegion

  const changeLocation = useCallback((newLocation: GlobalLocation) => {
    const { isRegion, radius } = newLocation
    const radiusOverride = isRegion ? undefined : radius || RADIUS_SLIDER_HALF_POINT_KMS
    setLocalLocation({ ...newLocation, radius: radiusOverride })
    setIsLoading(false)
    !isRegion && setSearchByRegionCheckboxValue(false)
  }, [])

  const setRegionCheckboxValue = useCallback(
    (newValue: boolean) => {
      setLocalLocation({ ...localLocation, isRegion: newValue })
      setSearchByRegionCheckboxValue(newValue)
    },
    [localLocation]
  )

  const setLoading = useCallback((newValue: boolean) => {
    setIsLoading(newValue)
  }, [])

  const onModalClose = useCallback(
    ({ wasLocationSet }: { wasLocationSet?: boolean }) => {
      if (!wasLocationSet) {
        trackEvent({ action: GA_EVENT.LocationSelectCancel })
        /* Reset the local modal location when user closes the modal without setting a new location */
        setLocalLocation(globalLocation)
      }
      setIsOpen(false)
      /* Reset the Search All checkbox state */
      setSearchByRegionCheckboxValue(false)
    },
    [globalLocation]
  )

  const setLocation = useCallback(
    (location: GlobalLocation, userModifiedRadius?: boolean) => {
      if (globalLocation && areLocationsEquivalent(globalLocation, location)) {
        onModalClose({ wasLocationSet: false })
        return
      }

      /** Update local storage if user has manually modified and submitted a radius value */
      if (userModifiedRadius && !location.isRegion) {
        setModifiedRadiusLocalStorage(true)
      }

      /** Check if user is on SRP */
      if (pathname.includes(`/srp/`)) {
        /** Refetch results for a region location */
        if (location.isRegion && isPositiveInteger(location.id)) {
          refetchResults(data, { selectedLocationId: location.id })
        } else {
          /** Refetch results for a radius location */
          refetchResults(data, { newArea: location })
        }
      } else {
        setGlobalLocation(location)
      }

      onModalClose({ wasLocationSet: true })
      trackEvent({ action: GA_EVENT.LocationSelectSuccess })
    },
    [data, globalLocation, onModalClose, pathname, refetchResults]
  )

  const commonLocationModalProps = {
    localLocation,
    onLocationChange: changeLocation,
    searchByRegionCheckboxValue,
  }

  return (
    <>
      {isOpen && (
        <StyledModal
          data-testid="search-location-modal"
          isCentered
          isOpen={isOpen}
          label={t('common:modals.search_location.title')}
          onCancel={() => onModalClose({ wasLocationSet: false })}
        >
          <>
            <LocationModalHeader
              handleClose={() => onModalClose({ wasLocationSet: false })}
              title={t('common:modals.search_location.title')}
            />

            <StyledModalBody>
              <LocationSearchBarWrapper>
                <LocationSearchBar
                  {...commonLocationModalProps}
                  id="select-location-search-bar"
                  label={t('modals.search_location.labels.search_input')}
                  isLoadingMap={isLoading}
                  onLocationSubmit={setLocation}
                  setLoading={setLoading}
                />
              </LocationSearchBarWrapper>
              <Map
                shouldShowMap={showMap}
                isRegion={!!localLocation.isRegion}
                latitude={localLocation.latitude}
                longitude={localLocation.longitude}
                radius={localLocation.radius || RADIUS_SLIDER_HALF_POINT_KMS}
                onLocationChange={changeLocation}
              />
              <RegionCheckbox
                {...commonLocationModalProps}
                setRegionCheckboxValue={setRegionCheckboxValue}
                showMap={showMap}
                setLoading={setLoading}
              />
            </StyledModalBody>

            <LocationModalFooter {...commonLocationModalProps} onLocationSubmit={setLocation} />
          </>
        </StyledModal>
      )}
      {children(() => setIsOpen(true))}
    </>
  )
}
